import React from "react";

import {
  FaCalendarAlt,
  FaCircle,
  FaEdit,
  FaEnvelope,
  FaMobileAlt,
  FaShieldAlt,
  FaTimes,
  FaUser,
  FaUserClock,
} from "react-icons/fa";

import "./UserDetailsDrawer.css";

const UserDetailsDrawer = ({
  open,
  onClose,
  user,
  onEdit,
  onChangeStatus,
}) => {
  if (!open || !user) {
    return null;
  }

  const normalizeStatus = (value) => {
    return String(value || "").toUpperCase();
  };

  const getStatusClass = (status) => {
    switch (normalizeStatus(status)) {
      case "ACTIVE":
        return "status-active";

      case "INACTIVE":
        return "status-inactive";

      case "BLOCKED":
        return "status-blocked";

      default:
        return "status-unknown";
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      ACTIVE: "Active",
      INACTIVE: "Inactive",
      BLOCKED: "Blocked",
    };

    return (
      labels[normalizeStatus(status)] ||
      "Unknown"
    );
  };

  const formatDate = (
    value,
    includeTime = true
  ) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return new Intl.DateTimeFormat(
      "en-IN",
      includeTime
        ? {
            dateStyle: "medium",
            timeStyle: "short",
          }
        : {
            dateStyle: "medium",
          }
    ).format(date);
  };

  const customerName =
    user?.name ||
    user?.fullName ||
    "Unknown Customer";

  const mobile =
    user?.mobile ||
    user?.phone ||
    "-";

  const email =
    user?.email || "-";

  const role = String(
    user?.role || "USER"
  ).toUpperCase();

  return (
    <>
      <button
        type="button"
        className="drawer-overlay"
        onClick={onClose}
        aria-label="Close customer details"
      />

      <aside
        className="user-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Customer details"
      >
        <div className="drawer-header">
          <div>
            <span>
              CUSTOMER PROFILE
            </span>

            <h2>
              Customer Details
            </h2>
          </div>

          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="drawer-profile">
          {user?.profileImage ? (
            <img
              src={user.profileImage}
              alt={customerName}
              className="profile-image"
              onError={(event) => {
                event.currentTarget.style.display =
                  "none";
              }}
            />
          ) : (
            <div className="profile-placeholder">
              <FaUser />
            </div>
          )}

          <h3>
            {customerName}
          </h3>

          <div
            className={`status-badge ${getStatusClass(
              user?.status
            )}`}
          >
            <FaCircle />

            {getStatusLabel(
              user?.status
            )}
          </div>

          <span className="user-drawer-role">
            {role === "USER"
              ? "Website Customer"
              : role}
          </span>
        </div>

        <div className="drawer-body">
          <section className="user-drawer-section">
            <h4>
              Contact Information
            </h4>

            <div className="detail-item">
              <FaMobileAlt />

              <div>
                <label>
                  Mobile Number
                </label>

                <span>
                  {mobile}
                </span>
              </div>
            </div>

            <div className="detail-item">
              <FaEnvelope />

              <div>
                <label>
                  Email Address
                </label>

                <span>
                  {email}
                </span>
              </div>
            </div>
          </section>

          <section className="user-drawer-section">
            <h4>
              Account Information
            </h4>

            <div className="detail-item">
              <FaShieldAlt />

              <div>
                <label>
                  Account Type
                </label>

                <span>
                  Website Customer
                </span>
              </div>
            </div>

            <div className="detail-item">
              <FaUserClock />

              <div>
                <label>
                  Verification
                </label>

                <span>
                  {user?.isVerified
                    ? "Account Verified"
                    : "Account Not Verified"}
                </span>

                <small>
                  Mobile:{" "}
                  {user?.mobileVerified
                    ? "Verified"
                    : "Not Verified"}
                </small>
              </div>
            </div>

            <div className="detail-item">
              <FaCalendarAlt />

              <div>
                <label>
                  Last Login
                </label>

                <span>
                  {formatDate(
                    user?.lastLogin
                  )}
                </span>
              </div>
            </div>
          </section>

          <section className="user-drawer-section">
            <h4>
              Timeline
            </h4>

            <div className="detail-item">
              <FaCalendarAlt />

              <div>
                <label>
                  Registered On
                </label>

                <span>
                  {formatDate(
                    user?.createdAt
                  )}
                </span>
              </div>
            </div>

            <div className="detail-item">
              <FaCalendarAlt />

              <div>
                <label>
                  Last Updated
                </label>

                <span>
                  {formatDate(
                    user?.updatedAt
                  )}
                </span>
              </div>
            </div>
          </section>

          <section className="user-drawer-section">
            <h4>
              Record Information
            </h4>

            <div className="detail-item user-id-detail">
              <FaUser />

              <div>
                <label>
                  Customer ID
                </label>

                <span>
                  {user?._id || "-"}
                </span>
              </div>
            </div>
          </section>
        </div>

        <div className="user-drawer-footer">
          <button
            type="button"
            className="user-drawer-status-button"
            onClick={() =>
              onChangeStatus(user)
            }
          >
            <FaShieldAlt />

            Change Status
          </button>

          <button
            type="button"
            className="user-drawer-edit-button"
            onClick={() =>
              onEdit(user)
            }
          >
            <FaEdit />

            Edit Customer
          </button>
        </div>
      </aside>
    </>
  );
};

export default UserDetailsDrawer;